// frontend/src/components/report/primitives/ScoreBenchmarkBar.js

// Signed difference between the business's own value and the regional
// median, formatted the same way everywhere it appears: "+16" ahead of the
// median, "−9" behind it, "±0" exactly on it. Exported so the arithmetic (and
// its sign) is tested directly instead of scraped out of rendered markup.
export function deltaLabel(value, median) {
  if (value == null || median == null) return null;
  const diff = Math.round(value - median);
  if (diff === 0) return "±0";
  return diff > 0 ? `+${diff}` : `−${Math.abs(diff)}`;
}

const pct = (v, max) => {
  const raw = ((Number(v) || 0) / max) * 100;
  return Math.max(0, Math.min(100, Math.round(raw * 100) / 100));
};

// One filled track — the score's own ScoreBenchmarkBar/ComparisonBar visual —
// plus an optional thin marker at the regional median, so "how far ahead or
// behind the region you are" reads as a picture next to the benchmarkLine
// sentence, not only as that sentence. `color` is the caller's existing
// tone.bar (a status color, not a brand color) — same convention as
// ComparisonBar/RadialScore: this file picks no color of its own. `caption`
// is the localized "Regionaler Median" wording (summaryCopy.js/
// i18n.js) — passed in so this file stays copy-free, like ComparisonBar.
export function ScoreBenchmarkBar({ value, max = 100, median, color, caption }) {
  const hasMedian = median != null;
  return (
    <div className="mt-3" data-testid="score-benchmark-bar">
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct(value, max)}%`, background: color }}
        />
        {hasMedian && (
          <div
            className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.35)]"
            style={{ left: `${pct(median, max)}%` }}
            aria-hidden="true"
            data-testid="score-benchmark-marker"
          />
        )}
      </div>
      {hasMedian && (
        // One plain string, not several separate JSX interpolations — so the
        // rendered markup is a single text node (matches T[lang].manualReviewed's
        // convention) instead of a value/label whose parts could be split apart
        // by adjacent-node handling.
        <p className="mt-1.5 text-sm tabular-nums text-muted-foreground" data-testid="score-benchmark-label">
          {`${caption}: ${median}/${max} (${deltaLabel(value, median)})`}
        </p>
      )}
    </div>
  );
}
